/**
 * SuccessCard Component
 *
 * Displays success message after post approval with links to published content.
 */

import PropTypes from "prop-types";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import Grid from "@mui/material/Grid";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import LinkedInIcon from "@mui/icons-material/LinkedIn";
import TwitterIcon from "@mui/icons-material/Twitter";
import ArticleIcon from "@mui/icons-material/Article";
import CloseIcon from "@mui/icons-material/Close";
import IconButton from "@mui/material/IconButton";

// Material Dashboard 2 React components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";

// Threads icon (custom since MUI doesn't have it)
const ThreadsIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
    <path d="M12.186 3.00003C9.43 3.00003 7.19 5.24003 7.19 8.00003C7.19 10.76 9.43 13 12.186 13C14.942 13 17.182 10.76 17.182 8.00003C17.182 5.24003 14.942 3.00003 12.186 3.00003ZM12.186 11C10.534 11 9.19 9.65603 9.19 8.00003C9.19 6.34403 10.534 5.00003 12.186 5.00003C13.838 5.00003 15.182 6.34403 15.182 8.00003C15.182 9.65603 13.838 11 12.186 11Z" />
    <path d="M19.186 8.00003C19.186 4.13403 16.052 1.00003 12.186 1.00003C8.32 1.00003 5.186 4.13403 5.186 8.00003C5.186 9.56403 5.718 11.006 6.614 12.162L2.186 21H8.186L10.686 16H13.686L16.186 21H22.186L17.758 12.162C18.654 11.006 19.186 9.56403 19.186 8.00003Z" />
  </svg>
);

function SuccessCard({ successData, onDismiss }) {
  const links = [
    {
      platform: "LinkedIn",
      url: successData?.linkedin_url,
      icon: <LinkedInIcon />,
      color: "#0077B5",
    },
    {
      platform: "X",
      url: successData?.x_url,
      icon: <TwitterIcon />,
      color: "#000000",
    },
    {
      platform: "Threads",
      url: successData?.threads_url,
      icon: <ThreadsIcon />,
      color: "#000000",
    },
    {
      platform: "Article",
      url: successData?.article_url,
      icon: <ArticleIcon />,
      color: "#4CAF50",
    },
  ].filter((link) => link.url); // Only show links that exist

  return (
    <MDBox
      sx={{
        position: "fixed",
        top: "50%",
        left: "50%",
        transform: "translate(-50%, -50%)",
        zIndex: 9999,
        width: { xs: "90%", sm: "600px" },
        maxWidth: "90vw",
      }}
    >
      <Card
        sx={{
          boxShadow: 24,
          animation: "slideIn 0.3s ease-out",
          "@keyframes slideIn": {
            from: {
              opacity: 0,
              transform: "scale(0.9)",
            },
            to: {
              opacity: 1,
              transform: "scale(1)",
            },
          },
        }}
      >
        <CardContent>
          {/* Close Button */}
          <MDBox display="flex" justifyContent="flex-end">
            <IconButton onClick={onDismiss} size="small">
              <CloseIcon />
            </IconButton>
          </MDBox>

          {/* Success Icon & Message */}
          <MDBox display="flex" flexDirection="column" alignItems="center" mb={4} mt={2}>
            <CheckCircleIcon sx={{ fontSize: 80, color: "success.main", mb: 2 }} />
            <MDTypography variant="h3" fontWeight="bold" textAlign="center" mb={1}>
              Successfully Posted!
            </MDTypography>
            <MDTypography variant="body1" color="text" textAlign="center">
              Your content has been published to all platforms
            </MDTypography>
          </MDBox>

          {/* Platform Links */}
          {links.length > 0 && (
            <Grid container spacing={2} mb={3}>
              {links.map((link) => (
                <Grid item xs={6} key={link.platform}>
                  <Card
                    sx={{
                      cursor: "pointer",
                      transition: "transform 0.2s, box-shadow 0.2s",
                      "&:hover": {
                        transform: "translateY(-4px)",
                        boxShadow: 3,
                      },
                    }}
                    onClick={() => window.open(link.url, "_blank")}
                  >
                    <MDBox p={2} display="flex" flexDirection="column" alignItems="center">
                      <MDBox
                        sx={{
                          color: link.color,
                          mb: 1,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                        }}
                      >
                        {link.icon}
                      </MDBox>
                      <MDTypography variant="button" fontWeight="medium">
                        View on {link.platform}
                      </MDTypography>
                    </MDBox>
                  </Card>
                </Grid>
              ))}
            </Grid>
          )}

          {/* Dismiss Button */}
          <MDBox display="flex" justifyContent="center">
            <MDButton variant="contained" color="info" onClick={onDismiss}>
              Done
            </MDButton>
          </MDBox>
        </CardContent>
      </Card>

      {/* Backdrop */}
      <MDBox
        sx={{
          position: "fixed",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          bgcolor: "rgba(0, 0, 0, 0.5)",
          zIndex: -1,
        }}
        onClick={onDismiss}
      />
    </MDBox>
  );
}

SuccessCard.propTypes = {
  successData: PropTypes.shape({
    linkedin_url: PropTypes.string,
    x_url: PropTypes.string,
    threads_url: PropTypes.string,
    article_url: PropTypes.string,
  }),
  onDismiss: PropTypes.func.isRequired,
};

SuccessCard.defaultProps = {
  successData: {},
};

export default SuccessCard;
